import { Button } from '@/components/ui-kit/button';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentArea } from './LetterheadUploadDialog';

interface ContentAreaEditorProps {
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  initialContentArea: ContentArea;
  onContentAreaChange: (area: ContentArea) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

// Default 1-inch margin (approximately 12% of page width for standard letter size)
const DEFAULT_HORIZONTAL_MARGIN_PERCENT = 0.12;

export function ContentAreaEditor({
  imageUrl,
  imageWidth,
  imageHeight,
  initialContentArea,
  onContentAreaChange,
  onConfirm,
  onCancel,
}: ContentAreaEditorProps) {
  const { t } = useTranslation();
  // Fixed horizontal margins (1-inch default)
  const fixedX = Math.round(imageWidth * DEFAULT_HORIZONTAL_MARGIN_PERCENT);
  const fixedWidth = Math.round(imageWidth * (1 - 2 * DEFAULT_HORIZONTAL_MARGIN_PERCENT));

  // Only y and height are adjustable
  const [contentArea, setContentArea] = useState<ContentArea>({
    x: fixedX,
    y: initialContentArea.y,
    width: fixedWidth,
    height: initialContentArea.height,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragHandle, setDragHandle] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Calculate scale to fit image in container — measure the actual container
  // so the preview adapts to phone-width dialogs (was hardcoded 600px, which
  // overflowed on <600px viewports and caused horizontal scroll).
  useEffect(() => {
    const calculateScale = () => {
      const el = containerRef.current;
      if (!el) return;
      const available = el.getBoundingClientRect();
      const maxWidth = Math.max(200, available.width - 32);
      const maxHeight = 500;
      const scaleX = maxWidth / imageWidth;
      const scaleY = maxHeight / imageHeight;
      setScale(Math.min(scaleX, scaleY, 1));
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [imageWidth, imageHeight]);

  const scaledWidth = imageWidth * scale;
  const scaledHeight = imageHeight * scale;
  const scaledArea = {
    x: contentArea.x * scale,
    y: contentArea.y * scale,
    width: contentArea.width * scale,
    height: contentArea.height * scale,
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>, handle?: string) => {
    e.preventDefault();
    // Pointer capture isn't strictly needed here because we listen on
    // `document` below, but it keeps the gesture predictable on touch.
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    setDragHandle(handle || 'move');
    const rect = imageContainerRef.current?.getBoundingClientRect();
    if (rect) {
      const px = (e.clientX - rect.left) / scale;
      const py = (e.clientY - rect.top) / scale;
      setDragStart({
        x: px - contentArea.x,
        y: py - contentArea.y,
      });
    }
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    setDragStart(null);
    setDragHandle(null);
  };

  useEffect(() => {
    if (isDragging) {
      // Pointer events cover both mouse and touch — needed so the handle is
      // draggable on phones/tablets, not just with a mouse.
      const handlePointerMoveGlobal = (e: PointerEvent) => {
        if (!imageContainerRef.current) return;
        const rect = imageContainerRef.current.getBoundingClientRect();

        const y = (e.clientY - rect.top) / scale;

        if (!dragStart || !dragHandle) return;

        const newArea = { ...contentArea };

        // Only allow vertical adjustments - x and width are fixed
        if (dragHandle === 'n') {
          // Adjust top edge
          const newY = Math.max(0, Math.min(y, contentArea.y + contentArea.height - 50));
          newArea.height = contentArea.y + contentArea.height - newY;
          newArea.y = newY;
        } else if (dragHandle === 's') {
          // Adjust bottom edge
          newArea.height = Math.max(50, Math.min(y - contentArea.y, imageHeight - contentArea.y));
        }
        // Note: 'move', corner handles, and horizontal handles are disabled

        setContentArea(newArea);
        onContentAreaChange(newArea);
      };

      document.addEventListener('pointermove', handlePointerMoveGlobal);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerUp);
      return () => {
        document.removeEventListener('pointermove', handlePointerMoveGlobal);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerUp);
      };
    }
  }, [
    isDragging,
    dragStart,
    dragHandle,
    contentArea,
    scale,
    imageWidth,
    imageHeight,
    onContentAreaChange,
  ]);

  return (
    <div className="flex flex-col gap-4">
      <div className="text-sm text-muted-foreground">
        {t('GENERATEREVIEW_CONTENTAREA_INSTRUCTIONS')}
      </div>

      <div
        ref={containerRef}
        className="relative border-2 border-dashed border-border rounded-lg bg-muted/20 p-3 sm:p-5 overflow-hidden flex items-center justify-center"
        style={{ minHeight: '320px', maxHeight: '550px' }}
        onPointerUp={handlePointerUp}
      >
        <div
          ref={imageContainerRef}
          className="relative mx-auto"
          style={{
            width: scaledWidth,
            height: scaledHeight,
          }}
        >
          <img
            src={imageUrl}
            alt={t('GENERATEREVIEW_LETTERHEAD_PREVIEWALT')}
            className="block"
            width={scaledWidth}
            height={scaledHeight}
            style={{
              width: scaledWidth,
              height: scaledHeight,
            }}
          />

          {/* Content area overlay - no move cursor since horizontal position is fixed */}
          <div
            className="absolute border-2 border-selise-blue bg-selise-blue/10"
            style={{
              left: scaledArea.x,
              top: scaledArea.y,
              width: scaledArea.width,
              height: scaledArea.height,
            }}
          >
            {/* Only top and bottom edge handles for vertical adjustment.
                Handles are tall enough (24px / w-20 h-6) to hit reliably with
                a finger; `touch-action: none` prevents page scroll while
                dragging on touch devices. */}
            <div
              className="absolute -top-3 left-1/2 -translate-x-1/2 w-20 h-6 bg-selise-blue border border-white rounded cursor-ns-resize flex items-center justify-center"
              style={{ touchAction: 'none' }}
              onPointerDown={(e) => {
                e.stopPropagation();
                handlePointerDown(e, 'n');
              }}
            >
              <div className="w-8 h-0.5 bg-white rounded" />
            </div>
            <div
              className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-20 h-6 bg-selise-blue border border-white rounded cursor-ns-resize flex items-center justify-center"
              style={{ touchAction: 'none' }}
              onPointerDown={(e) => {
                e.stopPropagation();
                handlePointerDown(e, 's');
              }}
            >
              <div className="w-8 h-0.5 bg-white rounded" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center text-sm">
        <div className="text-muted-foreground">
          {t('GENERATEREVIEW_CONTENTAREA_SUMMARY', {
            top: Math.round(contentArea.y),
            bottom: Math.round(imageHeight - contentArea.y - contentArea.height),
          })}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          {t('GENERATEREVIEW_CONTENTAREA_CANCEL')}
        </Button>
        <Button onClick={onConfirm}>{t('GENERATEREVIEW_CONTENTAREA_CONFIRM')}</Button>
      </div>
    </div>
  );
}
